import { useEffect, useState } from "react";
import { useModelSettings, useUpdateModelSettings } from "@/hooks/useModelSettings";
import { Button, PageHeader, Toggle } from "@/components/ui";

const chatModels = [
  { value: "gpt-4o-mini", label: "GPT-4o Mini" },
  { value: "gpt-4.1-mini", label: "GPT-4.1 Mini" },
  { value: "gpt-4.1-nano", label: "GPT-4.1 Nano" },
  { value: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
];

const embeddingModels = [
  { value: "text-embedding-3-small", label: "text-embedding-3-small" },
  { value: "text-embedding-3-large", label: "text-embedding-3-large" },
  { value: "text-embedding-ada-002", label: "text-embedding-ada-002" },
];

const SettingsModels = () => {
  const { data, isLoading } = useModelSettings();
  const updateMutation = useUpdateModelSettings();
  const [state, setState] = useState({
    chatModel: "gpt-4o-mini",
    embeddingModel: "text-embedding-3-small",
    temperature: 0.2,
    streaming: true,
    markdownEnabled: true,
  });

  useEffect(() => {
    if (data) {
      setState({
        chatModel: data.chatModel,
        embeddingModel: data.embeddingModel,
        temperature: data.temperature,
        streaming: data.streaming,
        markdownEnabled: data.markdownEnabled,
      });
    }
  }, [data]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateMutation.mutate(state);
  };

  return (
    <div className="page">
      <PageHeader
        label="Settings"
        title="Model Settings"
        description="Configure the AI models used for chat and embeddings."
      />

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-b">
            <div className="sec">
              <div className="row c2">
                <div className="fg">
                  <label className="fl">Chat Model</label>
                  <select
                    className="sel"
                    value={state.chatModel}
                    onChange={(e) => setState((prev) => ({ ...prev, chatModel: e.target.value }))}
                  >
                    {chatModels.map((model) => (
                      <option key={model.value} value={model.value}>
                        {model.label}
                      </option>
                    ))}
                  </select>
                  <p className="hint">Higher tier models increase quality at the expense of cost.</p>
                </div>

                <div className="fg">
                  <label className="fl">Embedding Model</label>
                  <select
                    className="sel"
                    value={state.embeddingModel}
                    onChange={(e) => setState((prev) => ({ ...prev, embeddingModel: e.target.value }))}
                  >
                    {embeddingModels.map((model) => (
                      <option key={model.value} value={model.value}>
                        {model.label}
                      </option>
                    ))}
                  </select>
                  <p className="hint">Ensure embeddings are consistent with stored vectors.</p>
                </div>
              </div>

              <div className="fg">
                <div className="lbl-row">
                  <label className="fl">Temperature</label>
                </div>
                <div className="sld-w">
                  <input
                    className="sld"
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={state.temperature}
                    onChange={(e) => setState((prev) => ({ ...prev, temperature: Number(e.target.value) }))}
                  />
                  <span className="sld-v">{state.temperature.toFixed(2)}</span>
                </div>
                <p className="hint">Control creativity vs. determinism. Lower values are more focused.</p>
              </div>
            </div>

            <div className="sec">
              <div className="sec-h">
                <b>Response Settings</b>
              </div>
              <div className="tg-row" style={{ borderBottom: "1px solid var(--line)" }}>
                <span className="tx">
                  <b>Streaming Responses</b>
                  <span>Show responses as they're generated.</span>
                </span>
                <Toggle
                  checked={state.streaming}
                  onChange={(checked) => setState((prev) => ({ ...prev, streaming: checked }))}
                />
              </div>
              <div className="tg-row">
                <span className="tx">
                  <b>Markdown Formatting</b>
                  <span>Allow headings, tables, and rich formatting.</span>
                </span>
                <Toggle
                  checked={state.markdownEnabled}
                  onChange={(checked) => setState((prev) => ({ ...prev, markdownEnabled: checked }))}
                />
              </div>
            </div>
          </div>

          <div className="card-f">
            <span className="meta">
              {data?.updatedAt ? (
                <>
                  Last updated {new Date(data.updatedAt).toLocaleString()}
                  {data.updatedBy && <> by <b style={{ color: "var(--ink-2)" }}>{data.updatedBy}</b></>}
                </>
              ) : isLoading ? (
                "Loading…"
              ) : null}
            </span>
            <div className="r">
              <Button type="submit" loading={updateMutation.isPending}>
                Save Settings
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SettingsModels;
